import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { GlobalSearch } from '../components/GlobalSearch'
import { NotificationBell } from '../components/NotificationBell'
import { IconButton, Menu, MenuDivider, MenuItem } from '../components/ui'
import { useIsCompactNarrow, type WindowClass } from './breakpoints'

/**
 * Search app bar M3 Expressive (64): icona menu solo in compact, ricerca globale (56, full),
 * azioni e avatar (32) con il menu dell'account. Nessun breadcrumb (D3): la voce attiva è nel rail
 * e il titolo nel PageHeader.
 */
export function AppBar({
  navClass, onMenu, themeIsDark, onQuickTheme, onThemePanel,
  userName, userRole, onLogout, menuDialogOpen = false, hasNavBar = false, forceNarrow = false, branchSlot,
}: {
  /** ADR-019 (Equal Point): selettore del ramo, sempre visibile accanto alle azioni. */
  branchSlot?: ReactNode | undefined
  navClass: WindowClass
  /** Con la navigation bar in basso l'icona hamburger dell'app bar sparisce (il menu e' nella barra). */
  hasNavBar?: boolean | undefined
  /** Pagina confinata in un segmento stretto (libro separante): stessa barra ridotta del compact-narrow. */
  forceNarrow?: boolean | undefined
  onMenu: () => void
  themeIsDark: boolean
  onQuickTheme: () => void
  onThemePanel: () => void
  userName: string
  userRole: string
  onLogout: () => void
  menuDialogOpen?: boolean | undefined
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const avatarRef = useRef<HTMLButtonElement>(null)
  const searchWrapRef = useRef<HTMLDivElement>(null)

  const initials = userName.split(' ').filter(Boolean).map(p => (p[0] ?? '').toUpperCase()).join('').slice(0, 2) || 'U'

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Navigazione avvenuta: menu e ricerca compatta si chiudono.
  useEffect(() => { setMenuOpen(false); setSearchOpen(false) }, [location.pathname, location.search])

  // Ricerca compatta: apertura -> focus sul campo; Esc la richiude.
  useEffect(() => {
    if (!searchOpen) return
    searchWrapRef.current?.querySelector('input')?.focus()
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setSearchOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [searchOpen])

  const compact = navClass === 'compact'
  // Sotto 360 (cover screen dei pieghevoli) tema e personalizzazione passano nel menu dell'account.
  const narrow = useIsCompactNarrow() || forceNarrow
  const searchTakesOver = compact && searchOpen

  return (
    <header className={'md-appbar' + (scrolled ? ' is-scrolled' : '')}>
      {searchTakesOver ? (
        <div className="md-appbar__searchrow" ref={searchWrapRef}>
          <IconButton icon="arrow_back" aria-label="Chiudi ricerca" onClick={() => setSearchOpen(false)} />
          <GlobalSearch />
        </div>
      ) : (
        <>
          {compact && !hasNavBar && (
            <IconButton icon="menu" aria-label="Apri menu" aria-haspopup="dialog" aria-expanded={menuDialogOpen} onClick={onMenu} />
          )}
          {!compact && <div className="md-appbar__search"><GlobalSearch /></div>}
          <div className="md-appbar__actions">
            {branchSlot}
            {compact && <IconButton icon="search" aria-label="Cerca" onClick={() => setSearchOpen(true)} />}
            {/* F-043 / REQ-267-001: indicatore sempre visibile delle notifiche non lette. */}
            <NotificationBell />
            {!narrow && <IconButton icon="palette" aria-label="Personalizza tema" onClick={onThemePanel} />}
            {!narrow && <IconButton icon={themeIsDark ? 'light_mode' : 'dark_mode'} aria-label="Cambia tema" onClick={onQuickTheme} />}
            <div className="md-appbar__user">
              <button
                ref={avatarRef}
                type="button"
                className="md-avatar md-appbar__avatar md-state"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={`Account di ${userName}`}
                onClick={() => setMenuOpen(o => !o)}
              >
                <span aria-hidden="true">{initials}</span>
              </button>
              <Menu anchorRef={avatarRef} open={menuOpen} onClose={() => setMenuOpen(false)} aria-label="Account">
                <div role="presentation" className="md-appbar__menuhead">
                  <span className="md-avatar" aria-hidden="true">{initials}</span>
                  <span className="md-appbar__menutext">
                    <span className="md-avatar-name">{userName}</span>
                    <span className="md-avatar-role">{userRole}</span>
                  </span>
                </div>
                <MenuDivider />
                {/* F-040 / REQ-032-002: punto d'ingresso al proprio profilo (sicurezza account, secondo fattore). */}
                {narrow && <MenuItem icon="palette" onSelect={onThemePanel}>Personalizza tema</MenuItem>}
                {narrow && <MenuItem icon={themeIsDark ? 'light_mode' : 'dark_mode'} onSelect={onQuickTheme}>Cambia tema</MenuItem>}
                {narrow && <MenuDivider />}
                <MenuItem icon="shield_person" onSelect={() => navigate('/account/security')}>Sicurezza account</MenuItem>
                <MenuItem icon="logout" onSelect={onLogout}>Esci</MenuItem>
              </Menu>
            </div>
          </div>
        </>
      )}
    </header>
  )
}
